
import React from 'react';
import { createPortal } from 'react-dom';
import { Coffee, Heart, Gift, X } from 'lucide-react';
import { Language } from '../types';
import { t } from '../locales';

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
}

const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose, language }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700">
        
        <div className="absolute top-4 right-4">
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
                <X size={20} />
            </button>
        </div>

        <div className="text-center mb-6">
            <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Heart size={32} fill="currentColor" className="animate-pulse" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{t(language, 'donateTitle')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t(language, 'donateDesc')}
            </p>
        </div>

        <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-all active:scale-95 group">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-gray-800 rounded-full text-orange-500 shadow-sm">
                        <Coffee size={20} />
                    </div>
                    <div className="text-left">
                        <span className="block font-bold text-gray-800 dark:text-white text-sm">{t(language, 'monthlyCoffee')}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">Recurring support</span>
                    </div>
                </div>
            </button>

            <button className="w-full flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all active:scale-95 group">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-gray-800 rounded-full text-emerald-500 shadow-sm">
                        <Gift size={20} />
                    </div>
                    <div className="text-left">
                        <span className="block font-bold text-gray-800 dark:text-white text-sm">{t(language, 'oneTimeDonation')}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">Single contribution</span>
                    </div>
                </div>
            </button>
            
            <div className="grid grid-cols-3 gap-2 mt-2">
                <button className="py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold hover:bg-gray-50 dark:hover:bg-gray-700">{t(language, 'donate5')}</button>
                <button className="py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold hover:bg-gray-50 dark:hover:bg-gray-700">{t(language, 'donate20')}</button>
                <button className="py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold hover:bg-gray-50 dark:hover:bg-gray-700">{t(language, 'donateMore')}</button>
            </div>
        </div>

        <p className="text-[10px] text-gray-400 text-center mt-6">
            Secure payment processing via Stripe. You can cancel monthly support at any time.
        </p>

      </div>
    </div>,
    document.body
  );
};

export default DonationModal;
